import type { CSSProperties } from 'react'
import { mark } from './tokens'
import horizontal from './marca/horizontal.svg'
import horizontalNegativa from './marca/horizontal-negativa.svg'
import simbolo from './marca/simbolo.svg'
import simboloNegativa from './marca/simbolo-negativa.svg'

/**
 * BrandMark — a assinatura Zentriz (cap. 04; ADR-001/003/008). Não aceita children: o respiro é parte do símbolo.
 *  - Sempre monocromática: positiva (tinta) sobre papel e sobre chapa Cura; negativa (papel) sobre tinta e sobre Construção.
 *  - Respiro = 1 nó = 2u = 20% da largura do SÍMBOLO, derivado do viewBox real de cada variante (verify-geometry confere).
 *  - Horizontal nunca abaixo de 120px (p.031); no header, fixa em 120px em todos os viewports (D2).
 */
type Variant = 'horizontal' | 'symbol'
type Chapa = 'papel' | 'tinta' | 'cura' | 'construcao'
// viewBox real dos arquivos: Horizontal 400×80 com o símbolo ocupando 79,5156; Símbolo 200×200
const GEOMETRY = { horizontal: { w: 400, h: 80, symbolW: 79.5156 }, symbol: { w: 200, h: 200, symbolW: 200 } } as const
const FILE: Record<Variant, Record<'positiva' | 'negativa', string>> = {
  horizontal: { positiva: horizontal, negativa: horizontalNegativa },
  symbol: { positiva: simbolo, negativa: simboloNegativa },
}
// ADR-008: sobre Cura a negativa mede 1,71:1 (reprova) → positiva; sobre Construção a negativa mede 6,51:1 → negativa
const VERSION_ON: Record<Chapa, 'positiva' | 'negativa'> = { papel: 'positiva', cura: 'positiva', tinta: 'negativa', construcao: 'negativa' }

export interface BrandMarkProps {
  variant?: Variant
  /** Sobre que chapa a assinatura está — decide positiva/negativa pela medição, não pelo gosto. */
  on: Chapa
  /** Largura em px. Horizontal: mínimo 120 (o componente não desce abaixo). */
  width?: number
  className?: string
}

export function BrandMark({ variant = 'horizontal', on, width, className }: BrandMarkProps) {
  const g = GEOMETRY[variant]
  const w = Math.max(variant === 'horizontal' ? mark.minWidthPx : 32, width ?? (variant === 'horizontal' ? mark.headerWidthPx : 48))
  const clear = (g.symbolW / g.w) * w * mark.clearRatioOfSymbol
  const style = { '--z-mark-w': `${w}px`, '--z-mark-clear': `${clear.toFixed(2)}px` } as CSSProperties
  return (
    <span className={['z-mark', className].filter(Boolean).join(' ')} style={style} data-brand-mark={variant} data-brand-on={on} data-anatomy="assinatura">
      <img src={FILE[variant][VERSION_ON[on]]} alt="Zentriz" width={w} height={Math.round((g.h / g.w) * w)} decoding="async" />
    </span>
  )
}

export default BrandMark
